import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Mail, RefreshCw, CheckCircle2, XCircle, Clock, AlertTriangle } from "lucide-react";

const STAGE_LABELS = {
  welcome_otp:        { label: "Welcome + OTP",         color: "#0044FF" },
  verified_welcome:   { label: "Verified — welcome",    color: "#10B981" },
  trial_expiring:     { label: "Trial expiring",        color: "#F97316" },
  trial_expiring_d3:  { label: "Trial · 3 days left",   color: "#F97316" },
  trial_expiring_d1:  { label: "Trial · 1 day left",    color: "#F97316" },
  trial_expiring_d0:  { label: "Trial ends today",      color: "#EF4444" },
  credit_low:         { label: "Credits low",           color: "#F59E0B" },
  inactivity:         { label: "30-day inactivity",     color: "#7C3AED" },
  deletion_scheduled: { label: "Deletion scheduled",    color: "#EF4444" },
};

export default function AdminEmails() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: s }, { data: r }] = await Promise.all([
        api.get("/admin/emails/summary"),
        api.get("/admin/emails", { params: filter ? { stage: filter } : {} }),
      ]);
      setSummary(s);
      setRows(r);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const total = Object.values(summary).reduce((acc, byStatus) =>
    acc + Object.values(byStatus).reduce((a, b) => a + b, 0), 0);

  return (
    <div data-testid="admin-emails-page" className="space-y-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display font-extrabold text-3xl">Email log</h1>
          <p className="text-sm text-[#4B5563] mt-1">Every transactional + lifecycle email the platform has sent. {total} total.</p>
        </div>
        <button onClick={load} className="btn-outline text-sm inline-flex items-center gap-2"><RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh</button>
      </div>

      {/* Per-stage summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        <StageTile label="All" stage="" color="#0F1115" filter={filter} setFilter={setFilter} summary={summary} />
        {Object.keys(STAGE_LABELS).map((stage) => (
          <StageTile key={stage} label={STAGE_LABELS[stage].label} stage={stage} color={STAGE_LABELS[stage].color} filter={filter} setFilter={setFilter} summary={summary} />
        ))}
      </div>

      <div className="hs-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F3F4F6] text-[11px] uppercase font-mono text-[#4B5563]">
            <tr>
              <th className="text-left px-4 py-3">Sent</th>
              <th className="text-left px-4 py-3">Stage</th>
              <th className="text-left px-4 py-3">To</th>
              <th className="text-left px-4 py-3">Subject</th>
              <th className="text-left px-4 py-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-sm text-[#4B5563]">No emails yet.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-[#E2E8F0] hover:bg-[#F9FAFB]" data-testid={`email-row-${r.id}`}>
                <td className="px-4 py-3 font-mono text-[11px] text-[#4B5563] whitespace-nowrap">
                  {r.sent_at ? new Date(r.sent_at).toLocaleString() : "—"}
                </td>
                <td className="px-4 py-3">
                  <span
                    className="inline-block px-2 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-widest"
                    style={{ background: `${(STAGE_LABELS[r.stage]?.color || "#4B5563")}14`, color: STAGE_LABELS[r.stage]?.color || "#4B5563" }}
                  >
                    {STAGE_LABELS[r.stage]?.label || r.stage}
                  </span>
                </td>
                <td className="px-4 py-3 font-mono">{r.to}</td>
                <td className="px-4 py-3">{r.subject}</td>
                <td className="px-4 py-3"><StatusBadge status={r.status} error={r.error} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StageTile({ label, stage, color, filter, setFilter, summary }) {
  const data = stage ? (summary[stage] || {}) : Object.values(summary).reduce((acc, s) => {
    Object.entries(s).forEach(([k, v]) => { acc[k] = (acc[k] || 0) + v; });
    return acc;
  }, {});
  const total = Object.values(data).reduce((a, b) => a + b, 0);
  const sent = data.sent || 0;
  const active = filter === stage;
  return (
    <button
      onClick={() => setFilter(stage)}
      className={`text-left p-4 rounded-[6px] border transition-all ${active ? "border-[#0F1115] shadow-sm" : "border-[#E2E8F0] hover:border-[#94A3B8]"}`}
      style={active ? { background: `${color}08` } : {}}
      data-testid={`stage-tile-${stage || "all"}`}
    >
      <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color }}>{label}</div>
      <div className="font-display font-extrabold text-2xl mt-1">{total}</div>
      <div className="text-[11px] font-mono text-[#4B5563] mt-1">{sent} delivered</div>
    </button>
  );
}

function StatusBadge({ status, error }) {
  const map = {
    sent:                 { Icon: CheckCircle2, color: "#10B981", label: "delivered" },
    skipped_unsubscribed: { Icon: Mail, color: "#7C3AED", label: "skipped · unsubscribed" },
    skipped_no_api_key:   { Icon: AlertTriangle, color: "#F59E0B", label: "skipped · no key" },
    exception:            { Icon: XCircle, color: "#EF4444", label: "error" },
  };
  const m = map[status] || { Icon: Clock, color: "#4B5563", label: status || "pending" };
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] font-mono" title={error || ""} style={{ color: m.color }}>
      <m.Icon className="w-3.5 h-3.5" />
      {m.label}
    </span>
  );
}
